import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {API_CONFIG, ERROR_MESSAGES} from '../config';
import {productService} from '../services/products/productService';

export const useProducts = (collectionId) => {
    const [products, setProducts] = useState(() => productService.getFallbackProducts());
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [source, setSource] = useState('demo');
    const [lastSyncedAt, setLastSyncedAt] = useState(null);
    const abortRef = useRef(null);

    const productOptions = useMemo(
        () => Array.from(products.values()).sort((left, right) => (left.sku || left.id).localeCompare(right.sku || right.id)),
        [products],
    );

    const syncProducts = useCallback(async () => {
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        if (!API_CONFIG.ENABLE_PRODUCT_SYNC) {
            setProducts(productService.getFallbackProducts());
            setSource('demo');
            setError(null);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const rawProducts = await productService.getProducts({collectionId});
            if (controller.signal.aborted) return;
            const normalizedResult = productService.normalizeProducts(rawProducts, {collection: collectionId || 'All'});
            setProducts(normalizedResult.products);
            setSource(normalizedResult.source);
            setLastSyncedAt(new Date());
        } catch (syncError) {
            if (controller.signal.aborted) return;
            setProducts(productService.getFallbackProducts());
            setSource('demo');
            setError(syncError.message || ERROR_MESSAGES.PRODUCT_SYNC_FAILED);
        } finally {
            if (!controller.signal.aborted) setIsLoading(false);
        }
    }, [collectionId]);

    useEffect(() => {
        syncProducts();
        return () => abortRef.current?.abort();
    }, [syncProducts]);

    return {
        products,
        productOptions,
        isLoading,
        error,
        source,
        lastSyncedAt,
        syncProducts,
    };
};
